import React, { useState, useEffect, createContext, useContext } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase"; // your initialized Firebase app

const TranslationContext = createContext();

// Add your static translations here
const translations = {
  en: {
    search: "Search",
    searchPlaceholder: "Search...",
    switchLanguage: "Switch Language",
    live: "LIVE",
    account: "Account",
    logout: "Logout",
    signIn: "Sign In",
    signUp: "Sign Up",
    loading: "Loading...",
    error: "Error occurred",
    // Add more translations as needed
    home: "Home",
    about: "About",
    contact: "Contact",
    settings: "Settings",
    heroDescription: "Your Source For Live Digital Broadcasts. Powered By",
    homeInstruction: "Select a thumbnail below to watch digital broadcasts",
    footerPowered: "Powered by",
    footerWebsite: "Website",
    footerWebsite2: "Previous Archives",
    footerAdmin: "Admin",
    footerCopyright: "Copyright ©2026 ISiLIVE | All Rights Reserved.",
    shortDescription: "Short description:",
    noShortDescripton: "No short description available",
    noShortDescription: "No short description available",
    longDescription: "Long description:",
    noLongDescription: "No long description available",
    currentlyPlaying: "Currently playing:",
    views: "Views:",
    videos: "Videos",
    notFound: "404 - Page Not Found",
    notFoundSorry: `Sorry, the page you're looking for doesn't exist.`,
    notFoundGoHome: "Go Home",
    noVideosAvailable: "No Videos Available",
  },
  fr: {
    search: "Rechercher",
    searchPlaceholder: "Rechercher...",
    switchLanguage: "Changer de langue",
    live: "EN DIRECT",
    account: "Compte",
    logout: "Déconnexion",
    signIn: "Se connecter",
    signUp: "S'inscrire",
    loading: "Chargement...",
    error: "Une erreur est survenue",
    // French translations
    home: "Accueil",
    about: "À propos",
    contact: "Contact",
    settings: "Paramètres",
    heroDescription:
      "Votre source pour les diffusions numériques en direct. Propulsé par",
    homeInstruction:
      "Sélectionnez une miniature ci-dessous pour regarder les diffusions numériques",
    footerPowered: "Propulsé par",
    footerWebsite: "Site Web",
    footerWebsite2: "Archives précédentes",
    footerAdmin: "Admin",
    footerCopyright: "Copyright ©2026 ISiLIVE | Tous droits réservés.",
    shortDescription: "Description courte:",
    noShortDescripton: "Aucune courte description disponible",
    noShortDescription: "Aucune courte description disponible",
    longDescription: "Description longue:",
    noLongDescription: "Aucune longue description disponible",
    currentlyPlaying: "Lecture en cours :",
    views: "Vues :",
    videos: "Vidéos",
    notFound: "404 - Page non trouvée",
    notFoundSorry: `Désolé, la page que vous recherchez n'existe pas.`,
    notFoundGoHome: "Retour à l'accueil",
    noVideosAvailable: "Aucune vidéo disponible",
  },
};

export const TranslationProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    return localStorage.getItem("language") || "en";
  });

  const setLanguage = (lang) => {
    setCurrentLanguage(lang);
    localStorage.setItem("language", lang);
  };

  const t = (key) => {
    return translations[currentLanguage][key] || key;
  };

  const functions = getFunctions(app, "us-central1"); // match REGION above
  const translateOnceFn = httpsCallable(functions, "translateOnce");

  // expose translateText using the callable (single text)
  const translateText = async (text, targetLang) => {
    if (!text || targetLang === "en") return text;
    try {
      const res = await translateOnceFn({ text, to: targetLang, source: "en" });
      return res?.data ?? text;
    } catch (e) {
      console.error("translateOnce failed:", e);
      return text;
    }
  };

  // optional: batch helper to reduce round-trips
  const translateBatch = async (arr, targetLang) => {
    if (!Array.isArray(arr) || !arr.length) return arr;
    try {
      const res = await translateOnceFn({
        text: arr,
        to: targetLang,
        source: "en",
      });
      return Array.isArray(res?.data) ? res.data : arr;
    } catch {
      return arr;
    }
  };

  const value = {
    currentLanguage,
    setLanguage,
    t,
    translateText,
    translateBatch, // optionally provide translateBatch
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error("useTranslation must be used within a TranslationProvider");
  }
  return context;
};
